import Vue from "vue";
import moment from "moment";
import {
    IAppointment,
    AppointmentStatus,
    AppointmentType,
    IAskAppointment,
    IExpedition,
    IProductionLine,
    IWorkOrder,
} from "@/models";

const toastDuration = 30000;

export const ToastHelper = {
    general: {
        invalidModel() {
            Vue.toasted.error("Informations manquantes !", {
                theme: "bubble",
                duration: 10000,
                position: "bottom-right",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        noCamera() {
            Vue.toasted.error("Pas de caméras disponibles !", {
                theme: "bubble",
                duration: 10000,
                position: "bottom-right",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },
    },

    appointments: {
        toastCreated(askAppointment: IAskAppointment) {
            let name = "";

            if (askAppointment.supplierId !== null) {
                name = askAppointment.supplier.name;
            } else {
                name = askAppointment.transporter.name;
            }

            Vue.toasted.info(`Camion ${name} sans-rendez-vous bien pris en compte !`, {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastUpdated(appointment: IAppointment) {
            let text = "";
            const formatTime = moment(appointment.expectedArrival).format("DD MMMM à HH:mm");

            if (appointment.transporterName !== null) {
                switch (appointment.status) {
                    case AppointmentStatus.Waiting:
                        text = `${AppointmentType[appointment.type].toUpperCase()} : Camion ${appointment.transporterName} du ${formatTime} est en attente d'arrivé !`;
                        break;
                    case AppointmentStatus.InProgress:
                        text = `${AppointmentType[appointment.type].toUpperCase()} : Camion ${appointment.transporterName} du ${formatTime} est arrivé !`;
                        break;
                    case AppointmentStatus.Done:
                        text = `${AppointmentType[appointment.type].toUpperCase()} : Camion ${appointment.transporterName} du ${formatTime} est terminé !`;
                        break;
                    case AppointmentStatus.Cancel:
                        text = `${AppointmentType[appointment.type].toUpperCase()} : Camion ${appointment.transporterName} du ${formatTime} est annulé !`;
                        break;
                }
            }
            else {
                text = `${AppointmentType[appointment.type].toUpperCase()} : Container du ${formatTime} est en attente d'arrivé`;
            }

            Vue.toasted.info(text, {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastOtherCreated() {
            Vue.toasted.info("Réunion ajoutée dans le planning !", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastOtherUpdated() {
            Vue.toasted.info("Réunion mise à jour dans le planning !", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastOtherDeleted() {
            Vue.toasted.info("Réunion supprimée du planning !", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },
    },

    receptions: {
        toastCreated(): void {
            Vue.toasted.info("Reception ajoutée !", {
                theme: "bubble",
                duration: null,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastDeleted(): void {
            Vue.toasted.info("Réception supprimée", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastConflict(): void {
            Vue.toasted.error("Impossible d'ajouter le camion, créneau déjà pris", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-right",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },
    },

    expeditions: {
        toastCreated(expedition: IExpedition): void {
            Vue.toasted.info("Expédition ajoutée", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastDeleted(): void {
            Vue.toasted.info("Expédition supprimée", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastFilled(expedition: IExpedition): void {
            Vue.toasted.info(
                `EXPEDITION : Fiche descriptive faite pour le camion ${expedition.transporter.name} prévu à ${moment(expedition.expectedArrival).format("HH:mm")} sur le ${expedition.dock}`, {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastConflict(): void {
            Vue.toasted.error("Impossible d'ajouter le camion, créneau déjà pris", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-right",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },
    },

    workOrders: {
        toastWorkOrderStarted(productionLine: IProductionLine, workOrder: IWorkOrder): void {
            Vue.toasted.info(`Nouvel OF ${workOrder.code} démarré sur la ligne ${productionLine.name}`, {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastWorkOrderMissionAdded(): void {
            Vue.toasted.info("Nouvelle mission", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastWorkOrderMissionAssigned(): void {
            Vue.toasted.info("Mission acceptée", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastWorkOrderMissionCanceled(): void {
            Vue.toasted.info("Mission annulée", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastRemindCleaning(): void {
            Vue.toasted.info("Faire le nettoyage", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastRemindCountPallets(): void {
            Vue.toasted.info("Faire le comptage des palettes", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastRemindCountConsumabled(): void {
            Vue.toasted.info("Faire le comptage des consommables", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastSynchronizeWorkOrders(): void {
            Vue.toasted.info("Synchronisation des OF en cours", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastSynchronizedWorkOrders(): void {
            Vue.toasted.info("OF mis à jour", {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },

        toastWorkOrderCommentAdded(workOrder: IWorkOrder): void {
            Vue.toasted.info(`Nouveau commentaire sur l'OF ${workOrder.code}`, {
                theme: "bubble",
                duration: toastDuration,
                position: "bottom-left",
                fitToScreen: true,
                closeOnSwipe: true,
                containerClass: "dfl-toasts",
                className: "dfl-toast",
            });
        },
    },
};
